import { useState } from 'react';
import { Check, Factory, Store, Wrench, type LucideIcon } from 'lucide-react';
import { ATIVIDADES, ROTULO_ATIVIDADE, type AtividadeEmpresa } from '@triade/contracts';
import { useAtividadesDaEmpresa, useSalvarAtividades } from '@/api/atividades';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api';
import { useSessao } from '@/lib/session';
import { cn } from '@/lib/utils';

function mensagemErro(e: unknown): string {
  return e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API';
}

const ICONE: Record<AtividadeEmpresa, LucideIcon> = { INDUSTRIA: Factory, COMERCIO: Store, SERVICO: Wrench };

/** O que muda no sistema quando a atividade esta marcada (texto da tela, sem regra de negocio). */
const O_QUE_MUDA: Record<AtividadeEmpresa, string> = {
  INDUSTRIA: 'Fichas técnicas, centros de custo, rateio e plano de produção',
  COMERCIO: 'Itens de mercadoria, aquisição, lista de preços e orçamentos',
  SERVICO: 'Serviços com composição de horas e orçamentos',
};

/**
 * Empresa > Parametros > Perfil de atividade: o que a empresa faz (Indústria, Comércio, Serviço). So muda o que aparece nos
 * menus e nos tipos de item; nada e apagado. Quem nao tem parametros.editar ve o estado, desabilitado (mesmo `editavel` do pai).
 */
export function AtividadesEmpresa({ editavel }: { editavel: boolean }) {
  const { empresaId } = useSessao();
  const atuais = useAtividadesDaEmpresa();
  return (
    <section aria-label="Perfil de atividade" data-testid="secao-atividades-empresa" className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-5">
      <div>
        <h2 className="text-title font-semibold">Perfil de atividade</h2>
        <p className="mt-1 text-small text-ink-secondary">O que a sua empresa faz? Isso muda só o que aparece nos menus e nos cadastros; nada é apagado.</p>
      </div>
      {atuais ? (
        <Escolha key={`${empresaId}:${atuais.join(',')}`} atuais={atuais} editavel={editavel} />
      ) : (
        <p role="status" className="text-small text-ink-muted">
          Carregando perfil de atividade…
        </p>
      )}
    </section>
  );
}

function Escolha({ atuais, editavel }: { atuais: readonly AtividadeEmpresa[]; editavel: boolean }) {
  const [marcadas, setMarcadas] = useState<AtividadeEmpresa[]>([...atuais]);
  const salvar = useSalvarAtividades();
  const vazio = marcadas.length === 0;
  const erro = salvar.isError ? mensagemErro(salvar.error) : null;

  function alternar(atividade: AtividadeEmpresa, ligado: boolean) {
    setMarcadas(ATIVIDADES.filter((a) => (a === atividade ? ligado : marcadas.includes(a))));
  }

  return (
    <div className="flex flex-col gap-3">
      <div role="group" aria-label="Atividades da empresa" className="grid gap-3 md:grid-cols-3">
        {ATIVIDADES.map((a) => {
          const marcada = marcadas.includes(a);
          const Icone = ICONE[a];
          return (
            <button
              key={a}
              type="button"
              role="checkbox"
              aria-checked={marcada}
              aria-labelledby={`atividade-${a}-titulo`}
              aria-describedby={`atividade-${a}-muda`}
              disabled={!editavel || salvar.isPending}
              onClick={() => alternar(a, !marcada)}
              className={cn(
                'flex flex-col gap-2 rounded-2xl border bg-card p-4 text-left transition-colors focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-60',
                marcada ? 'border-primary' : 'border-border hover:border-ink-muted',
              )}
            >
              <span className="flex items-center justify-between">
                <Icone aria-hidden="true" className={cn('size-5', marcada ? 'text-primary' : 'text-ink-secondary')} />
                {marcada && <Check aria-hidden="true" className="size-4 text-primary" />}
              </span>
              <span id={`atividade-${a}-titulo`} className="text-body font-semibold">
                {ROTULO_ATIVIDADE[a]}
              </span>
              <span id={`atividade-${a}-muda`} className="text-caption text-ink-muted">
                O que muda: {O_QUE_MUDA[a]}
              </span>
            </button>
          );
        })}
      </div>
      {vazio && (
        <p role="note" className="text-caption text-ink-muted">
          Escolha ao menos uma atividade
        </p>
      )}
      <div>
        <Button type="button" aria-label="Gravar atividades" disabled={!editavel || vazio || salvar.isPending} onClick={() => salvar.mutate(ATIVIDADES.filter((a) => marcadas.includes(a)))}>
          Gravar atividades
        </Button>
      </div>
      {erro && (
        <p role="alert" className="text-small text-danger-ink">
          {erro}
        </p>
      )}
    </div>
  );
}
